import React, { useCallback } from 'react';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { Grid, type SxProps, type Theme } from '@mui/material';
import Typography from '@mui/material/Typography';

import { Else, If, Then } from '../If';

interface NamedItem {
    name: string;
    id: string;
}

interface UniqueNameProps {
    sx?: SxProps<Theme>;
}

export const UniqueName = ({ sx }: UniqueNameProps = {}) => {
    const {
        pageState: { formData, pageConfig },
        pageActions
    } = usePageStore();
    const [itemList, setItemList] = React.useState<NamedItem[]>([]);

    const getItems = useCallback(() => {
        lediumApi.get<NamedItem[]>(`${pageConfig.apiEndPoint}/`).then((data) => {
            if (data) {
                setItemList(data);
            }
        });
    }, [pageConfig.apiEndPoint]);

    const checkUniqueName = useCallback(
        (value: string) => !itemList.some((e) => e.name.toLowerCase() === value.toLowerCase() && formData.id !== e.id),
        [itemList, formData.id]
    );

    React.useEffect(() => {
        getItems();
    }, [getItems]);

    React.useEffect(() => {
        ValidatorForm.addValidationRule('checkUniqueName', checkUniqueName);
        return () => ValidatorForm.removeValidationRule('checkUniqueName');
    }, [checkUniqueName]);

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        pageActions.formData.updateFormItem({ name: event.target.value });
    }

    return (
        <Grid item xs={12} sx={sx}>
            <If condition={!!formData.readOnly}>
                <Then>
                    <Typography variant="h3">{formData.name as string}</Typography>
                </Then>
                <Else>
                    <TextValidator
                        fullWidth
                        label={i18n('name')}
                        variant="outlined"
                        onChange={handleChange}
                        value={formData.name ?? ''}
                        validators={['required', 'checkUniqueName']}
                        errorMessages={[i18n('this_field_is_required'), i18n('the_name_should_be_unique_X_is_already_used', formData.name as string)]}
                    />
                </Else>
            </If>
        </Grid>
    );
};
